import { useMemo, useState, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  EditableCollectionBinding,
  useCollectionBinding,
  useContentToken,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface CategoryGroup {
  category: string;
  items: Array<{ faq: FAQ; globalIndex: number }>;
}

const UNCATEGORIZED_KEY = '__uncategorized__';

function groupByCategory(items: FAQ[]): CategoryGroup[] {
  const groups = new Map<string, Array<{ faq: FAQ; globalIndex: number }>>();
  const order: string[] = [];

  items.forEach((faq, i) => {
    const key = (faq.category || '').trim() || UNCATEGORIZED_KEY;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push({ faq, globalIndex: i });
  });

  return order.map((key) => ({
    category: key === UNCATEGORIZED_KEY ? '' : key,
    items: groups.get(key)!,
  }));
}

interface AccordionItemProps {
  faq: FAQ;
  itemId: string;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ faq, itemId, isOpen, onToggle }: AccordionItemProps) {
  const panelId = `${itemId}-panel`;
  const buttonId = `${itemId}-button`;

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <h3 className="faq-item-heading">
        <button
          type="button"
          id={buttonId}
          className="faq-item-trigger"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="faq-item-question">{faq.question || 'Question'}</span>
          <span className="faq-item-icon" aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="faq-item-panel"
        hidden={!isOpen}
      >
        <div
          className="faq-item-answer"
          dangerouslySetInnerHTML={{ __html: faq.answer || '' }}
        />
      </div>
    </div>
  );
}

export function FAQList() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const emptyMessage = useContentToken('faqlist.emptyMessage');

  const faqs = useCollectionBinding<FAQ>('faqlist.items', {
    collection: 'faqs',
  });

  const [openId, setOpenId] = useState<string | null>(null);

  const toggleItem = useCallback((id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  const groups = useMemo(() => groupByCategory(faqs), [faqs]);
  const hasAnyCategory = groups.some((g) => g.category !== '');

  const emptyText = emptyMessage || 'No FAQs are available at this time. Please check back soon.';

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up faq-section pt-2 md:pt-4 pb-12 md:pb-16 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
          {faqs.length > 0 ? (
            <div className="faq-groups">
              {groups.map((group, gIdx) => (
                <div key={group.category || `group-${gIdx}`} className="faq-group">
                  {hasAnyCategory && group.category && (
                    <h3 className="faq-group-heading">{group.category}</h3>
                  )}
                  <div className="faq-list">
                    {group.items.map(({ faq, globalIndex }) => {
                      const id = `faq-${gIdx}-${globalIndex}`;
                      return (
                        <AccordionItem
                          key={id}
                          itemId={id}
                          faq={faq}
                          isOpen={openId === id}
                          onToggle={() => toggleItem(id)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EditableToken
              path="faqlist.emptyMessage"
              as="p"
              className="faq-empty"
            >
              {emptyText}
            </EditableToken>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
          padding: 0 0 0.6rem;
          position: relative;
          text-align: left;
        }
        .faq-group-heading::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 2.5rem;
          height: 3px;
          background: var(--vibe-color-primary);
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .faq-item {
          border: 2px solid var(--vibe-color-primary);
          background: white;
          transition: box-shadow 0.25s ease;
        }
        .faq-item--open {
          box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.18);
        }

        .faq-item-heading {
          margin: 0;
          font-size: inherit;
          font-weight: inherit;
        }

        .faq-item-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1rem 1.25rem;
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          line-height: 1.4;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .faq-item-trigger:hover,
        .faq-item-trigger:focus-visible {
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
          outline: none;
        }
        .faq-item-trigger:focus-visible {
          box-shadow: inset 0 0 0 2px var(--vibe-color-primary);
        }

        .faq-item-question {
          flex: 1;
          min-width: 0;
        }

        .faq-item-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          flex-shrink: 0;
          color: var(--vibe-color-primary);
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.2s ease;
        }
        .faq-item--open .faq-item-icon {
          transform: rotate(180deg);
          background: var(--vibe-color-primary);
          color: white;
        }

        .faq-item-panel {
          overflow: hidden;
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          animation: faq-slide 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .faq-item-panel[hidden] {
          display: none;
        }
        @keyframes faq-slide {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .faq-item-panel {
            animation: none;
          }
          .faq-item-icon {
            transition: none;
          }
        }

        .faq-item-answer {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          padding: 1rem 1.25rem 1.25rem;
        }
        .faq-item-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-item-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-item-answer ul,
        .faq-item-answer ol {
          padding-left: 1.5rem;
          margin: 0 0 0.75rem;
        }
        .faq-item-answer li {
          margin-bottom: 0.35rem;
        }
        .faq-item-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-item-answer strong {
          color: var(--vibe-color-text);
        }

        .faq-empty {
          font-family: var(--vibe-font-body);
          text-align: center;
          color: var(--vibe-color-text-muted);
          padding: 2rem 0;
        }

        @media (max-width: 640px) {
          .faq-group-heading {
            text-align: center;
          }
          .faq-group-heading::after {
            left: 50%;
            transform: translateX(-50%);
          }
          .faq-item-trigger {
            padding: 0.875rem 1rem;
            font-size: 1rem;
          }
          .faq-item-answer {
            padding: 0.875rem 1rem 1rem;
            font-size: 0.9375rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.emptyMessage': {
      label: 'Empty State Message',
      type: 'text',
      defaultValue: 'No FAQs are available at this time. Please check back soon.',
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
